import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { KnowledgeBaseInterface, KnowledgeSource, KnowledgeSourceConfigType } from '@vendasta/embeddings';
import { AiKnowledgeService } from '../services/ai-knowledge.service';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { Router, RouterModule } from '@angular/router';
import { KnowledgeListComponent } from '../knowledge-list/knowledge-list.component';
import { MatDialog, MatDialogConfig, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ACCOUNT_GROUP_ID_TOKEN, SHOW_BUSINESS_PROFILE_SOURCE_TOKEN } from '../tokens';
import { AiKnowledgeI18nModule } from '../assets/i18n/ai-knowledge-i18n.module';
import { MatDividerModule } from '@angular/material/divider';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { firstValueFrom, map, switchMap, take } from 'rxjs';
import { isSameKnowledgeSource, KnowledgeSourceWithSuggestedFlag } from '../ai-knowledge-util';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { AddKnowledgeComponent } from '../knowledge-editor/add-knowledge/add-knowledge.component';
import { WebsiteScrapeComponent } from '../knowledge-editor/website-scrape/website-scrape.component';
import { CustomDataComponent } from '../knowledge-editor/custom-data/custom-data.component';
import { BusinessProfileComponent } from '../knowledge-editor/business-profile/business-profile.component';
import { FileUploadComponent } from '../knowledge-editor/file-upload/file-upload.component';
import { StepChangeEvent } from '../knowledge-editor/common/interfaces';
import { FileTypeIconService } from '../services/file-type-icon.service';
import { LoadingState } from '../interface/loading-state';

/**
 * Every app (AI assistant, web chat widget, social-marketing app, …) gets an implicit "default" knowledge collection
 * holding its directly-attached knowledge sources. Embeddings ids these `KB-<appID>`, and app ids are always
 * `APP-<TYPE>-…` (`APP-AIASSISTANT-`, `APP-WEBCHAT-`, `APP-SM-`), so every app-default KB is prefixed `KB-APP-`;
 * user-created collections are `KB-<uuid>` and never collide. These defaults are managed as knowledge sources and
 * must never be offered as shared knowledge, so they're filtered out of the collections list everywhere here.
 */
const APP_DEFAULT_KB_ID_PREFIX = 'KB-APP-';

/**
 * Tagged wrapper for autocomplete option values so a single (optionSelected) handler can tell a knowledge *source*
 * apart from a knowledge *collection* without inspecting shape. Selecting the static "add new" option passes no value.
 */
type KnowledgeOption =
  | { kind: 'source'; source: KnowledgeSourceWithSuggestedFlag }
  | { kind: 'base'; base: KnowledgeBaseInterface };

@Component({
  selector: 'ai-knowledge-application-knowledge',
  imports: [
    RouterModule,
    TranslateModule,
    AiKnowledgeI18nModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    FormsModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    GalaxyLoadingSpinnerModule,
    GalaxyFormFieldModule,
    KnowledgeListComponent,
    MatDialogModule,
    MatDividerModule,
    GalaxyAlertModule,
    GalaxyBadgeModule,
    MatCheckboxModule,
  ],
  providers: [AiKnowledgeService],
  templateUrl: './application-knowledge.component.html',
  styleUrls: ['./application-knowledge.component.scss'],
})
export class ApplicationKnowledgeComponent implements OnInit {
  @Input() appId!: string;
  @Input() disabled = false;
  readonly enabled = input(true);

  /**
   * When true, shared knowledge collections are loaded and shown as extra rows in the same list, and the "Add
   * knowledge" search matches collections alongside sources. Requires {@link appId}. Off by default so surfaces
   * that only manage sources (e.g. the platform-assistant settings) are unchanged.
   */
  readonly includeKnowledgeBases = input(false);

  readonly selectedSourcesChange = output<{ sources: KnowledgeSource[]; initialLoad?: boolean }>();
  readonly selectedKnowledgeBasesChange = output<{ knowledgeBases: KnowledgeBaseInterface[]; initialLoad?: boolean }>();
  readonly loadStatusChange = output<LoadingState>();

  DIALOG_WIDTH = '660px';
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly bpObserver = inject(BreakpointObserver);
  readonly knowledgeService = inject(AiKnowledgeService);
  private readonly fileTypeIconService = inject(FileTypeIconService);

  private readonly accountGroupID = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  private readonly showBusinessProfileSource = toSignal(inject(SHOW_BUSINESS_PROFILE_SOURCE_TOKEN));

  private readonly accountGroupService: AccountGroupService | undefined;

  private suggestedWebsiteURL = '';
  private hasWebsiteKS = false;

  sources = signal<KnowledgeSourceWithSuggestedFlag[]>([]);
  searchValue = signal<string>('');
  selectedSources = signal<{ sources: KnowledgeSourceWithSuggestedFlag[]; initialLoad: boolean } | null>(null);
  filteredSources = computed(() => {
    const selected = this.selectedSources();
    return this.sources().filter(
      (source) =>
        source.name.toLowerCase().indexOf(this.searchValue()) >= 0 &&
        !(selected?.sources ?? []).some((s) => isSameKnowledgeSource(s, source)),
    );
  });

  // Shared knowledge collections (only populated when `includeKnowledgeBases` is on).
  allKnowledgeBases = signal<KnowledgeBaseInterface[]>([]);
  selectedKnowledgeBases = signal<KnowledgeBaseInterface[]>([]);
  /** Candidate collections to connect: has an id, not a primary collection, not already selected, matching the search. */
  filteredKnowledgeBases = computed(() => {
    if (!this.includeKnowledgeBases()) {
      return [];
    }
    const selectedIds = new Set(this.selectedKnowledgeBases().map((kb) => kb.id));
    const search = this.searchValue();
    return this.allKnowledgeBases().filter(
      // `!!kb.id` mirrors loadKnowledgeBases: a zero-id KB (proto3 default) would otherwise be selectable and
      // put '' into selectedIds, hiding every other zero-id KB from the autocomplete.
      (kb) =>
        !this.isAppDefaultKb(kb) && !!kb.id && !selectedIds.has(kb.id) && (kb.name ?? '').toLowerCase().includes(search),
    );
  });

  readonly loadStatus = signal<LoadingState>('loading');

  selectedSourceControl = new FormControl();

  @ViewChild('knowledgeSourceInput') knowledgeSourceInput!: ElementRef<HTMLInputElement>;
  @ViewChild('auto') matAutocomplete!: MatAutocomplete;
  @ViewChild('trigger') matAutocompleteTrigger!: MatAutocompleteTrigger;

  constructor(private readonly destroyRef: DestroyRef) {
    if (this.accountGroupID()) {
      this.accountGroupService = inject(AccountGroupService);
    }
    effect(() => {
      const sources = this.selectedSources();
      if (sources) {
        this.selectedSourcesChange.emit(sources);
      }
    });
    effect(() => {
      this.loadStatusChange.emit(this.loadStatus());
    });
    effect(() => {
      if (this.enabled()) {
        this.selectedSourceControl.enable({ emitEvent: false });
      } else {
        this.selectedSourceControl.disable({ emitEvent: false });
      }
    });

    effect(async () => {
      const accountGroupID = this.knowledgeService.accountGroupId();
      const partnerID = this.knowledgeService.partnerId();
      if (accountGroupID || partnerID) {
        await this.initialize();
      }
    });

    // Load shared collections in a dedicated effect that tracks the grants flag as well as the ids to avoid fetching the
    // same resources multiple times. The call to `loadKnowledgeBases` can be moved into initialize when the feature flag is released.
    effect(() => {
      const accountGroupID = this.knowledgeService.accountGroupId();
      const partnerID = this.knowledgeService.partnerId();
      const includeKnowledgeBases = this.includeKnowledgeBases();
      if (includeKnowledgeBases && (accountGroupID || partnerID)) {
        this.loadKnowledgeBases().catch((err) =>
          console.error('[application-knowledge] failed to load knowledge collections', err),
        );
      }
    });
  }

  private async refreshSelectedSources(initialLoad?: boolean): Promise<void> {
    let initialSources: KnowledgeSourceWithSuggestedFlag[] = [];
    if (this.appId) {
      initialSources = await this.knowledgeService.listAllKnowledgeSourcesForApp(this.appId);
    } else if (this.showBusinessProfileSource()) {
      const businessProfileSource = this.knowledgeService.buildBusinessProfileKnowledgeSource(true);
      if (businessProfileSource) {
        initialSources.push(businessProfileSource);
      }

      if (this.suggestedWebsiteURL && !this.hasWebsiteKS) {
        const businessWebsiteSource = this.knowledgeService.buildBusinessWebsiteKnowledgeSource(
          this.suggestedWebsiteURL,
          true,
        );
        if (businessWebsiteSource) {
          initialSources.push(businessWebsiteSource);
        }
      }
    }
    this.selectedSources.set({ sources: initialSources, initialLoad: !!initialLoad });
  }

  async ngOnInit(): Promise<void> {
    this.selectedSourceControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((searchValue) => {
      if (typeof searchValue === 'string') {
        this.searchValue.set(searchValue?.toLowerCase());
      }
    });
  }

  async initialize() {
    this.loadStatus.set('loading');
    try {
      await this.checkWebsiteKnowledgeSource();
      await this.refreshSources();
      await this.refreshSelectedSources(true);
      this.loadStatus.set('succeeded');
    } catch {
      this.loadStatus.set('failed');
    }
  }

  /**
   * Loads all connectable collections and marks the ones already connected to this app as selected. The full list is
   * loaded (and therefore searchable) even before the app exists — mirroring how knowledge sources are always listed.
   * Which collections are *already connected* can only be known once there's an app, so that step is skipped when
   * `appId` is empty (a not-yet-saved assistant starts with none connected).
   */
  private async loadKnowledgeBases(): Promise<void> {
    if (!this.includeKnowledgeBases()) {
      return;
    }
    const [connectedKeys, allKnowledgeBases] = await Promise.all([
      this.appId ? this.knowledgeService.listKnowledgeBaseKeysForApp(this.appId) : Promise.resolve([]),
      // Include collections shared into this namespace iff knowledge grants are enabled. `includeKnowledgeBases`
      // is bound to the grants-enabled flag by the parent (and we've already returned early when it's false), so
      // it's the right signal for the cross-namespace opt-in — cross-namespace results are meaningless with grants off.
      this.knowledgeService.listKnowledgeBases(false, this.includeKnowledgeBases()),
    ]);
    this.allKnowledgeBases.set(allKnowledgeBases);

    const connectedIds = new Set(connectedKeys.map((key) => key.knowledgeBaseId));
    const connected = allKnowledgeBases.filter(
      (kb) => !!kb.id && connectedIds.has(kb.id) && !this.isAppDefaultKb(kb),
    );
    this.selectedKnowledgeBases.set(connected);
    this.selectedKnowledgeBasesChange.emit({ knowledgeBases: connected, initialLoad: true });
  }

  /** An app's default collection (its private, source-backed KB) — never offered as shared knowledge. */
  private isAppDefaultKb(kb: KnowledgeBaseInterface): boolean {
    return !!kb.id && kb.id.startsWith(APP_DEFAULT_KB_ID_PREFIX);
  }

  // Checks if a website source is available and if there is a website URL available
  // Register all results in local variables
  async checkWebsiteKnowledgeSource() {
    const accountGroupId = this.accountGroupID();
    if (this.showBusinessProfileSource() && this.accountGroupService && accountGroupId) {
      // Gets the website URL from the account group
      const accountGroup = await firstValueFrom(
        this.accountGroupService.get(accountGroupId, new ProjectionFilter({ napData: true })),
      );
      this.suggestedWebsiteURL = accountGroup?.napData?.website ?? '';

      // Checks if there are any website sources
      const existingWebsiteKnowledgeSources = await this.knowledgeService.listKnowledgeSources(
        [KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_WEBSITE_SCRAPE],
        { pageSize: 1 },
      );
      this.hasWebsiteKS = existingWebsiteKnowledgeSources?.knowledgeSources?.length > 0;
    }
  }

  async refreshSources(): Promise<void> {
    // TODO: actually page and search on value changes
    const knowledgeSources = await this.knowledgeService.listAllKnowledgeSources([
      KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_CUSTOM_DATA,
      KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_WEBSITE_SCRAPE,
      KnowledgeSourceConfigType.KNOWLEDGE_SOURCE_CONFIG_TYPE_FILE,
    ]);
    if (this.showBusinessProfileSource()) {
      // manually insert business profile source because it may not exist yet
      const businessProfileSource = this.knowledgeService.buildBusinessProfileKnowledgeSource(true);
      if (businessProfileSource) {
        knowledgeSources.unshift(businessProfileSource);
      }

      // Adds a suggestion to add a new website source if there are none available and we have a website URL
      if (this.suggestedWebsiteURL && !this.hasWebsiteKS) {
        const businessWebsiteSource = this.knowledgeService.buildBusinessWebsiteKnowledgeSource(
          this.suggestedWebsiteURL,
          true,
        );
        if (businessWebsiteSource) {
          knowledgeSources.unshift(businessWebsiteSource);
        }
      }
    }
    this.sources.set(knowledgeSources);
    this.clearInputFields();
  }

  async forceRefreshSources(): Promise<void> {
    this.loadStatus.set('loading');
    try {
      await this.refreshSources();
      this.loadStatus.set('succeeded');
    } catch {
      this.loadStatus.set('failed');
    }
  }

  async viewSource(source: KnowledgeSourceWithSuggestedFlag): Promise<void> {
    const conf: MatDialogConfig = {
      width: this.DIALOG_WIDTH,
      data: {
        knowledgeSourceKey: { knowledgeSourceId: source?.id, namespace: source?.namespace },
        excludeAddToAIStep: true,
        config: source?.config,
        isSuggested: source?.isSuggested,
        currentAppId: this.appId,
      },
    };

    const isSmallScreen = this.bpObserver.isMatched(Breakpoints.XSmall);
    if (isSmallScreen) {
      conf.maxWidth = '100vw';
      conf.maxHeight = '100vh';
    }

    let editor: MatDialogRef<StepChangeEvent>;
    if (source?.config?.websiteScrapeConfig) {
      editor = this.dialog.open(WebsiteScrapeComponent, conf);
    } else if (source?.config?.customDataConfig) {
      editor = this.dialog.open(CustomDataComponent, conf);
    } else if (source?.config?.fileConfig) {
      editor = this.dialog.open(FileUploadComponent, conf);
    } else {
      editor = this.dialog.open(BusinessProfileComponent, conf);
    }
    const navigatingAway = await firstValueFrom(editor.afterClosed());
    if (!navigatingAway && !source?.isSuggested) {
      this.loadStatus.set('loading');
      const updatedKnowledge = await this.knowledgeService.getKnowledgeSource(source?.id, source?.namespace);
      this.selectedSources.update((current) => {
        if (!current) {
          return current;
        }
        const index = current.sources.findIndex((item) => isSameKnowledgeSource(item, source));
        if (index !== -1) {
          current.sources[index] = updatedKnowledge;
        }
        return current;
      });
      this.loadStatus.set('succeeded');
    }
  }

  setSelectedSources(sources: KnowledgeSource[]): void {
    this.selectedSources.set({ sources, initialLoad: false });
  }

  removeSource(source: KnowledgeSource): void {
    if (!this.enabled()) {
      return;
    }
    this.selectedSources.update((selectedSources) => {
      if (!selectedSources) {
        return { sources: [], initialLoad: false };
      }
      const index = selectedSources.sources.indexOf(source);
      if (index >= 0) {
        selectedSources.sources.splice(index, 1);
      }
      return { sources: [...selectedSources.sources], initialLoad: false };
    });
  }

  addSource(source: KnowledgeSource | undefined): void {
    if (!this.enabled()) {
      return;
    }
    if (!source) {
      this.addKnowledge();
      return;
    }

    this.selectedSources.update((selectedSources) => {
      const existingSources = selectedSources?.sources ?? [];
      const alreadySelected = existingSources.some((s) => isSameKnowledgeSource(s, source));
      if (!alreadySelected) {
        existingSources.push(source);
      }
      this.clearInputFields();
      this.matAutocompleteTrigger.closePanel();
      return { sources: [...existingSources], initialLoad: false };
    });
  }

  /** Dispatches an autocomplete selection to the right handler; no value means the static "add new knowledge" option. */
  onKnowledgeOptionSelected(option: KnowledgeOption | undefined): void {
    if (!option) {
      this.addKnowledge();
      return;
    }
    if (option.kind === 'base') {
      this.addKnowledgeBase(option.base);
    } else {
      this.addSource(option.source);
    }
  }

  chooseMatch(event: any): void {
    event.preventDefault();
    const sources = this.filteredSources();
    const bases = this.filteredKnowledgeBases();
    // Only auto-select on Enter when the search narrows to exactly one match across sources and collections.
    if (sources.length + bases.length !== 1) {
      return;
    }
    if (sources.length === 1) {
      this.addSource(sources[0]);
    } else {
      this.addKnowledgeBase(bases[0]);
    }
  }

  addKnowledgeBase(kb: KnowledgeBaseInterface | undefined): void {
    if (!this.enabled() || !kb) {
      return;
    }
    if (this.selectedKnowledgeBases().some((selected) => selected.id === kb.id)) {
      this.clearInputFields();
      return;
    }
    const next = [...this.selectedKnowledgeBases(), kb];
    this.selectedKnowledgeBases.set(next);
    this.selectedKnowledgeBasesChange.emit({ knowledgeBases: next, initialLoad: false });
    this.clearInputFields();
    this.matAutocompleteTrigger?.closePanel();
  }

  removeKnowledgeBase(kb: KnowledgeBaseInterface): void {
    if (!this.enabled()) {
      return;
    }
    const next = this.selectedKnowledgeBases().filter((selected) => selected.id !== kb.id);
    this.selectedKnowledgeBases.set(next);
    this.selectedKnowledgeBasesChange.emit({ knowledgeBases: next, initialLoad: false });
  }

  clearInputFields(): void {
    if (this.knowledgeSourceInput?.nativeElement?.value) {
      this.knowledgeSourceInput.nativeElement.value = '';
    }
    this.selectedSourceControl.setValue('');
  }

  async addKnowledge(): Promise<void> {
    if (!this.enabled()) {
      return;
    }
    const conf: MatDialogConfig = {
      width: this.DIALOG_WIDTH,
      data: { excludeAddToAIStep: true },
    };

    const isSmallScreen = this.bpObserver.isMatched(Breakpoints.XSmall);
    if (isSmallScreen) {
      conf.maxWidth = '100vw';
      conf.maxHeight = '100vh';
    }
    const editor = this.dialog.open(AddKnowledgeComponent, conf);
    editor.componentInstance.componentSelected
      .pipe(takeUntilDestroyed(editor.componentInstance.destroyRef))
      .subscribe((event) => {
        this.subscribeToKnowledgeCreated(event.knowledgeCreated, event.destroyRef);
      });
    await firstValueFrom(editor.afterClosed());
  }

  private subscribeToKnowledgeCreated(event: EventEmitter<string>, destroyRef: DestroyRef): void {
    event
      .pipe(
        takeUntilDestroyed(destroyRef),
        take(1),
        switchMap((sourceId) =>
          this.knowledgeService.getKnowledgeSource(sourceId, this.knowledgeService.buildNamespaceInterface(), false),
        ),
        map((ks) => {
          this.selectedSources.update((selectedSources) => {
            return { sources: [...(selectedSources?.sources ?? []), ks], initialLoad: false };
          });
        }),
        switchMap(() => this.forceRefreshSources()),
      )
      .subscribe();
  }

  getKnowledgePreview(source: KnowledgeSource): string {
    return this.knowledgeService.getKnowledgePreview(source);
  }

  getKnowledgeSourceIcon(source: KnowledgeSource): string {
    return this.fileTypeIconService.getKnowledgeSourceIcon(source);
  }
}
